import {useLightbox} from 'platform/lightbox';
import {match} from 'ts-pattern';

import {useEffect} from 'react';

import {isNotNil} from 'ramda';

import {sourcingRoutes} from '@dms/routes';

import {composePath, Nullish, useNavigate} from 'shared/web';

import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {SourcingTableDispatch} from '../../../store/sourcingTable/reducer';
import {Row} from '../types/Row';
import {SourcingTableName} from '../types/SourcingTableName';
import {createRowElementId} from '../utils/createRowElementId';
import {isKeyboardEventFromEditableField} from '../utils/isKeyboardEventFromEditableField';
import {sourcingTableSessionStorage} from '../utils/sourcingTableSessionStorage';

export function useHandleKeyboards(
  selectedRow: Row | Nullish,
  data: Row[],
  tableName: SourcingTableName,
  dispatch: SourcingTableDispatch
) {
  const navigate = useNavigate();
  const sourcingType = useSourcingType();
  const [, {isOpen: isLightboxOpen}] = useLightbox('sourcingVehicleGallery');

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isLightboxOpen) {
        return;
      }

      if (isKeyboardEventFromEditableField(e)) {
        return;
      }

      const currentIndex = data.findIndex(
        (row) =>
          isNotNil(selectedRow) && row.sourcingVehicle?.adId === selectedRow.sourcingVehicle?.adId
      );

      match(e.key)
        .with('ArrowDown', () => {
          const row = data[currentIndex + 1] ?? selectedRow;
          dispatch({type: 'selectRow', row});
          sourcingTableSessionStorage.setLastVisitedVehicleId(tableName, row.sourcingVehicle.adId);
          window.document
            .getElementById(createRowElementId(row))
            ?.scrollIntoView({block: 'nearest'});
        })
        .with('ArrowUp', () => {
          const row = data[currentIndex - 1] ?? selectedRow;
          dispatch({type: 'selectRow', row});
          sourcingTableSessionStorage.setLastVisitedVehicleId(tableName, row.sourcingVehicle.adId);
          window.document
            .getElementById(createRowElementId(row))
            ?.scrollIntoView({block: 'nearest'});
        })
        .with('ArrowRight', () => {
          if (isNotNil(selectedRow)) {
            dispatch({type: 'expandRow', row: selectedRow, isExpanded: true});
          }
        })
        .with('ArrowLeft', () => {
          if (isNotNil(selectedRow)) {
            dispatch({type: 'expandRow', row: selectedRow, isExpanded: false});
          }
        })
        .with('Enter', () => {
          const adId = selectedRow?.sourcingVehicle?.adId;
          if (isNotNil(adId)) {
            navigate(
              composePath(
                sourcingType === SourcingType.AUCTIONS
                  ? sourcingRoutes.auctionDetail
                  : sourcingRoutes.vehicleDetail,
                {params: {adId}}
              )
            );
          }
        });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [data, selectedRow, dispatch, tableName, navigate, isLightboxOpen, sourcingType]);
}
